import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Button, Chip, Skeleton, Table } from "@heroui/react";
import { Icon } from "@iconify/react";
import { dt } from "@/shared/helper/datetime";

import { useBanners } from "@/features/banners/use-banners";
import { BannerDrawer, DeleteBannerModal } from "@/features/banners";
import { BannerEntity } from "@/features/banners/types";
import { PageLayout } from "@/shared/components/page-layout.ui.tsx";

export const Route = createFileRoute("/dashboard/banners")({
  component: RouteComponent,
});

function RouteComponent() {
  const { data, isLoading } = useBanners();

  const [createOpen, setCreateOpen] = useState(false);
  const [editBanner, setEditBanner] = useState<BannerEntity | null>(null);
  const [deleteBanner, setDeleteBanner] = useState<BannerEntity | null>(null);

  return (
    <>
      <PageLayout
        actions={
          <Button
            isDisabled={isLoading}
            variant="primary"
            onPress={() => setCreateOpen(true)}
          >
            Создать баннер
            <Icon className="size-5" icon="solar:add-circle-bold" />
          </Button>
        }
        title="Баннеры"
      >
        {isLoading ? (
          <div className="flex flex-col gap-2 px-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full rounded-lg" />
            ))}
          </div>
        ) : (
          <Table.Root>
            <Table.Content aria-label="Список баннеров">
              <Table.Header>
                <Table.Column>ID</Table.Column>
                <Table.Column>Изображение</Table.Column>
                <Table.Column>Название</Table.Column>
                <Table.Column>Тип</Table.Column>
                <Table.Column>Категория</Table.Column>
                <Table.Column>Статус</Table.Column>
                <Table.Column>Дата создания</Table.Column>
                <Table.Column> </Table.Column>
              </Table.Header>

              <Table.Body items={data.data}>
                {(banner: BannerEntity) => (
                  <Table.Row id={String(banner.id)}>
                    <Table.Cell>{banner.id}</Table.Cell>
                    <Table.Cell>
                      {banner.image ? (
                        <img
                          alt={banner.title}
                          className="w-12 h-12 object-cover rounded"
                          src={banner.image}
                        />
                      ) : (
                        <div className="w-12 h-12 rounded bg-neutral-100 flex items-center justify-center">
                          <Icon
                            className="size-5 text-neutral-400"
                            icon="solar:image-broken"
                          />
                        </div>
                      )}
                    </Table.Cell>
                    <Table.Cell>{banner.title}</Table.Cell>
                    <Table.Cell>{banner.type}</Table.Cell>
                    <Table.Cell>{banner.category ?? "—"}</Table.Cell>
                    <Table.Cell>
                      <Chip
                        color={banner.isActive ? "success" : "danger"}
                        variant="soft"
                      >
                        {banner.isActive ? "Активен" : "Неактивен"}
                      </Chip>
                    </Table.Cell>
                    <Table.Cell>
                      {banner.createdAt
                        ? dt(banner.createdAt).format("DD.MM.YYYY")
                        : "—"}
                    </Table.Cell>
                    <Table.Cell>
                      <div className="flex flex-row gap-2 justify-end">
                        <Button
                          isIconOnly
                          variant="ghost"
                          onPress={() => setEditBanner(banner)}
                        >
                          <Icon
                            className="size-5"
                            icon="solar:pen-bold-duotone"
                          />
                        </Button>
                        <Button
                          isIconOnly
                          variant="danger-soft"
                          onPress={() => setDeleteBanner(banner)}
                        >
                          <Icon
                            className="size-5"
                            icon="solar:trash-bin-minimalistic-bold"
                          />
                        </Button>
                      </div>
                    </Table.Cell>
                  </Table.Row>
                )}
              </Table.Body>
            </Table.Content>
          </Table.Root>
        )}
      </PageLayout>

      <BannerDrawer
        isOpen={createOpen}
        mode="create"
        onOpenChange={setCreateOpen}
      />

      {editBanner && (
        <BannerDrawer
          banner={editBanner}
          isOpen={!!editBanner}
          mode="edit"
          onOpenChange={(open) => {
            if (!open) setEditBanner(null);
          }}
        />
      )}

      {deleteBanner && (
        <DeleteBannerModal
          banner={deleteBanner}
          isOpen={!!deleteBanner}
          onOpenChange={(open) => {
            if (!open) setDeleteBanner(null);
          }}
        />
      )}
    </>
  );
}
